<script module>
  import { parse as parseDuration } from 'duration-fns'

  import { gql } from '$lib/graphql.svelte.js'

  /** @typedef {{ travelDayCount?: number|Schema.Maybe<number>, validityPeriod?: string }} Validity */

  export const i18n = {
    en: {
      YOUTH: 'Youth',
      SENIOR: 'Senior',
      ADULT: 'Adult',
      CHILD: 'Child',
      '2ndclass': '2nd class',
      '1stclass': '1st class',
      '%s days': {
        one: '%s day',
        other: '%s days'
      },
      '%s months': {
        one: '%s month',
        other: '%s months'
      },
      'within %s months': {
        one: 'within %s month',
        other: 'within %s months'
      }
    },
    sv: {
      YOUTH: 'Ungdom',
      SENIOR: 'Senior',
      ADULT: 'Vuxen',
      CHILD: 'Barn',
      '2ndclass': '2:a klass',
      '1stclass': '1:a klass',
      '%s days': {
        one: '%s dag',
        other: '%s dagar'
      },
      '%s months': {
        one: '%s månad',
        other: '%s månader'
      },
      'within %s months': {
        one: 'inom %s månad',
        other: 'inom %s månader'
      },
      'in a row': 'i följd',
      'Select Passengers': 'Välj resenärer',
      'Select Comfort': 'Välj komfort',
      Summary: 'Sammanfattning',
      Total: 'Totalt',
      Proceed: 'Fortsätt',
      Preparing: 'Förbereder',
      Continuous: 'Löpande',
      'Not found': 'Hittades inte',
      "Sorry, we couldn't find a pass matching your selection.":
        'Tyvärr, vi kunde inte hitta ett pass som matchar ditt val.'
    },
    da: {
      YOUTH: 'Ungdom',
      SENIOR: 'Senior',
      ADULT: 'Voksen',
      CHILD: 'Barn',
      '2ndclass': '2. klasse',
      '1stclass': '1. klasse',
      '%s days': {
        one: '%s dag',
        other: '%s dage'
      },
      '%s months': {
        one: '%s måned',
        other: '%s måneder'
      },
      'within %s months': {
        one: 'indenfor %s måned',
        other: 'indenfor %s måneder'
      },
      'in a row': 'ubegrænset',
      'Select Passengers': 'Vælg passagerer',
      'Select Comfort': 'Vælg komfort',
      Summary: 'Oversigt',
      Proceed: 'Fortsæt',
      Preparing: 'Forbereder',
      'Not found': 'Ikke fundet',
      "Sorry, we couldn't find a pass matching your selection.":
        'Beklager, vi kunne ikke finde et pas, der matcher dit valg.'
    }
  }

  export const EURAIL_COUNTRY_NAMES = {
    austria: 'AT',
    benelux: 'Benelux',
    bulgaria: 'BU',
    croatia: 'HR',
    'czech-republic': 'CZ',
    denmark: 'DK',
    // 'estonia': 'ES',
    finland: 'FI',
    france: 'FR',
    germany: 'DE',
    greece: 'GR',
    'great-britain': 'GB',
    // 'greek-islands': 'Greek Islands',
    hungary: 'HU',
    ireland: 'IR',
    italy: 'IT',
    // 'latvia': 'LV',
    lithuania: 'LT',
    'north-macedonia': 'North Macedonia',
    norway: 'NO',
    poland: 'PL',
    portugal: 'PT',
    romania: 'RO',
    serbia: 'RS',
    slovakia: 'SK',
    slovenia: 'SI',
    spain: 'ES',
    sweden: 'SE',
    switzerland: 'CH',
    turkey: 'TR'
  }

  export const EURAIL_PRODUCT_REGEX =
    /^(interrail|eurail|german-rail)(?:-(global|one-country))?-pass(?:-([\w-]+))?/

  export const PassBundlesFragment = gql`
    fragment passBundle on PassBundle {
      id
      class
      product
      price {
        amount
        currency
      }
      travelDayCount
      validityPeriod
      passengers {
        count
        type
      }
    }
  `

  /** @param {Validity} [bundle] */
  export function getValidityKey(bundle) {
    if (!bundle) return
    const { travelDayCount, validityPeriod } = bundle
    return [travelDayCount, validityPeriod].filter(Boolean).join('-')
  }

  /**
   * @param {Validity} validity
   * @param {import('$lib/text.js').Text} $text
   */
  export function getBundleTravelDays(
    { travelDayCount, validityPeriod },
    $text
  ) {
    if (!validityPeriod) return $text`${travelDayCount} days`
    const { days, months } = parseDuration(validityPeriod)
    return travelDayCount || days
      ? $text`${travelDayCount || days} days`
      : $text`${months} months`
  }

  /**
   * @param {Validity} validity
   * @param {import('$lib/text.js').Text} $text
   */
  export function getBundleValidity({ travelDayCount, validityPeriod }, $text) {
    if (!validityPeriod) return $text`in a row`
    const { days, months } = parseDuration(validityPeriod)
    return travelDayCount === days || !travelDayCount
      ? $text`in a row`
      : days
        ? $text`within ${days} days`
        : $text`within ${months} months`
  }

  /**
   * @param {(Schema.PassBundle|Schema.PassBundleOrderItem|Schema.PassBundleSelection)?} bundle
   * @param {import('$lib/text.js').Text} $text
   * @returns {string?}
   */
  export function getProductName(bundle, $text) {
    if (!bundle) return null

    const match = bundle.product.match(EURAIL_PRODUCT_REGEX)
    if (match) {
      const [, type, kind, country] = match
      const category = bundle.travelDayCount == null ? 'Continuous' : 'Flexi'
      if (type === 'interrail') {
        if (kind === 'global') return `Interrail Global ${$text(category)} Pass`
        if (kind === 'one-country') {
          return `Interrail One Country ${$text(category)} Pass, ${$text(
            EURAIL_COUNTRY_NAMES[country]
          )}`
        }
      } else if (type === 'eurail') {
        if (kind === 'global') return `Eurail Global ${$text(category)} Pass`
        if (kind === 'one-country') {
          return `Eurail One Country ${$text(category)} Pass, ${$text(
            EURAIL_COUNTRY_NAMES[country]
          )}`
        }
      } else if (type === 'german-rail') {
        return `German Rail ${$text(category)} Pass`
      }
    }

    return null
  }
</script>

<script>
  import { money, WITHOUT_DECIMALS } from '$lib/money.js'
  import { register } from '$lib/utils/text.svelte.js'
  import Passengers from '$lib/Passengers.svelte'
  import DateInput from '$lib/DateInput.svelte'
  import Tooltip from '$lib/Tooltip.svelte'
  import Loader from '$lib/Loader.svelte'
  import Symbol from '$lib/Symbol.svelte'
  import Button from '$lib/Button.svelte'
  import Modal from '$lib/Modal.svelte'
  import Html from '$lib/Html.svelte'

  /**
   * @param {Schema.PassPassengerInput[]} passengers
   * @param {string?} [departureDate]
   * @param {'age'|'birthDate'} [inputMode]
   */
  const validate = (passengers, departureDate, inputMode) => {
    return (
      passengers.length > 0 &&
      passengers.every((passenger) =>
        inputMode === 'age'
          ? passenger.type === 'ADULT' || passenger.age != null
          : inputMode === 'birthDate'
            ? passenger.birthDate != null && departureDate != null
            : passenger.type === 'ADULT' ||
              passenger.age != null ||
              (passenger.birthDate != null && departureDate != null)
      )
    )
  }

  /**
   * Sanitize the passengers array to ensure that the birthDate and age are not null
   * @param {Schema.PassPassengerInput[]} passengers
   * @returns {Schema.PassPassengerInput[]}
   */
  const sanitizePassengers = (passengers) => {
    return passengers.map((passenger) => {
      const { birthDate, age, type } = passenger
      return { birthDate: birthDate ?? undefined, age: age ?? undefined, type }
    })
  }

  let text = register(i18n)

  /**
   * @typedef {Object} Props
   * @property {Boolean} [loading] - Whether the pass bundle parent form is loading
   * @property {string} currency - The currency to use for the pass bundle
   * @property {Schema.PassBundle} [placeholder] - The pass bundle to use as a placeholder before a selection is made
   * @property {'center'|'top'} [position] - The position of the modal
   * @property {string} [departureDate] - The departure date to use for the pass bundle (applicable for passengerInoutMode="birthDate")
   * @property {'age'|'birthDate'} [passengerInputMode] - The mode to use for passenger input
   * @property {Schema.PassPassengerInput[]} [passengers] - The passengers to use for the pass bundle
   * @property {Schema.PassBundleFilterInput} [filter] - The filter to use for the pass bundle
   * @property {string} [passClass] - The class to preselect (defaults to placeholder.class)
   * @property {number} [travelDayCount] - Display pass matching the travel day count (defaults to placeholder.travelDayCount)
   * @property {string} [validityPeriod] - Display pass matching the validity period (defaults to placeholder.validityPeriod)
   * @property {function(Schema.PassBundle): void} [onselect] - The function to call when a pass bundle is selected
   * @property {function(): void} [onclose] - The function to call when the modal is closed
   * @property {Svelte.Snippet} [children] - The children to render in the modal, typically used to display promos
   * @property {Svelte.Snippet} [credit] - The credit to render in the modal
   * @property {Svelte.Snippet} [error] - The error to render in the modal
   */

  /** @type {Props} */
  let {
    loading,
    currency,
    placeholder,
    departureDate,
    position = 'center',
    passengerInputMode = 'age',
    passengers = $bindable([{ type: 'ADULT' }]),
    filter = { product: placeholder?.product },
    passClass = placeholder?.class || '2ndclass',
    travelDayCount = placeholder?.travelDayCount || undefined,
    validityPeriod = placeholder?.validityPeriod || undefined,
    onselect,
    onclose,
    children,
    credit,
    error
  } = $props()

  /** @type {App.GraphQLOperation<{ bundles: Schema.PassBundle[] }, { passengers: Schema.PassPassengerInput[], currency: string, filter?: Schema.PassBundleFilterInput, departureDate?: string }>} */
  const GetPassBundles = gql`
    query GetPassBundles(
      $passengers: [PassPassengerInput!]!
      $filter: PassBundleFilterInput
      $currency: String
      $departureDate: Date
    ) {
      bundles: getPassBundles(
        passengers: $passengers
        currency: $currency
        filter: $filter
        departureDate: $departureDate
      ) {
        ...${PassBundlesFragment}
      }
    }
  `

  $effect(() => {
    if (validate(passengers, departureDate)) {
      return GetPassBundles.execute({
        passengers: sanitizePassengers(passengers),
        departureDate,
        currency,
        filter
      })
    }
  })

  let validityKey = $derived(
    getValidityKey(placeholder || { travelDayCount, validityPeriod })
  )
  let allBundleClasses = $derived(
    GetPassBundles.data?.bundles
      .filter((bundle) => getValidityKey(bundle) === validityKey)
      .sort((a, b) => a.price.amount - b.price.amount)
  )

  /** @type {string} */
  let selectedClass = $state(passClass)

  /** @type {Schema.PassBundle|undefined} */
  let bundle = $derived(
    allBundleClasses?.find((bundle) => bundle.class === selectedClass) ||
      placeholder
  )

  let intermediate = $state(false)

  let disabled = $derived(
    Boolean(
      loading ||
        intermediate ||
        GetPassBundles.fetching ||
        GetPassBundles.error ||
        !validate(passengers, departureDate)
    )
  )
</script>

<Modal {position} on:close={() => onclose?.()}>
  <div class="pass-bundle-builder">
    {#if !bundle && !GetPassBundles.fetching}
      <Html>
        <h2>{text`Not found`}</h2>
        <p>
          {text`Sorry, we couldn't find a pass matching your selection.`}
        </p>
      </Html>
    {:else}
      <div>
        <h2 class="title">
          {#if bundle}
            {getProductName(bundle, text)}
          {:else if GetPassBundles.fetching}
            <Loader length={8} />
          {/if}
        </h2>
        <p class="validity">
          {#if bundle}
            {getBundleTravelDays(bundle, text)}
            {getBundleValidity(bundle, text)}
          {:else if GetPassBundles.fetching}
            <Loader length={8} />
          {/if}
        </p>
      </div>
      <div class="body">
        <div class="panel">
          <fieldset
            aria-label="passengers-label"
            disabled={loading || GetPassBundles.fetching}>
            <legend class="legend">{text`Select Passengers`}</legend>
            <Passengers
              size="sm"
              max={Infinity}
              bind:passengers
              bind:intermediate
              minBirthDate={departureDate}
              inputMode={passengerInputMode} />
            {#if passengerInputMode === 'birthDate'}
              <div class="departure-date">
                <DateInput
                  required
                  id="departureDate"
                  name="departureDate"
                  value={departureDate}
                  min={new Date().toISOString().split('T')[0]}
                  onchange={(value) => (departureDate = value)}>
                  {#snippet label()}
                    <span class="aligned">
                      <strong>{text`Departure date`}</strong>
                      <Tooltip
                        text="The optimal pass will be calulated depending on age when traveling">
                        <Symbol
                          name="question"
                          size="sm"
                          circle
                          --current-color="rgb(var(--rgb-text))"
                          --current-background="rgb(var(--rgb-light-gray))" />
                      </Tooltip>
                    </span>
                  {/snippet}
                </DateInput>
              </div>
            {/if}
          </fieldset>
        </div>
        <div class="divider"></div>
        <div class="panel">
          <fieldset disabled={loading || GetPassBundles.fetching}>
            <legend class="legend">{text`Select Comfort`}</legend>
            <div class="options">
              {#if allBundleClasses && bundle}
                {#each allBundleClasses as bundleOption}
                  {@const checked =
                    passengers.length > 0 &&
                    bundleOption.class === bundle?.class}
                  {@const diff =
                    bundleOption.price.amount -
                    allBundleClasses[0].price.amount}
                  {@const price = money(
                    {
                      amount: diff,
                      currency: bundle.price.currency
                    },
                    WITHOUT_DECIMALS
                  )}
                  <label class="option" for={bundleOption.id}>
                    <input
                      {checked}
                      class="radio u-hidden"
                      type="radio"
                      name="passBundleId"
                      id={bundleOption.id}
                      value={bundleOption.id}
                      {disabled}
                      onchange={() =>
                        (selectedClass = /** @type {string} */ (
                          bundleOption.class
                        ))} />
                    <span class="label">
                      {text(bundleOption.class)}
                      <strong class="price">
                        {#if GetPassBundles.stale}
                          <Loader length={2} />
                        {:else}
                          +{price}
                        {/if}
                      </strong>
                      {#if checked}
                        <span class="symbol">
                          <Symbol name="checkmark" />
                        </span>
                      {/if}
                    </span>
                  </label>
                {/each}
              {:else}
                <div class="option">
                  <span class="label">
                    <Loader length={2} />
                    <Loader length={4} />
                  </span>
                </div>
              {/if}
            </div>
          </fieldset>
          {@render children?.()}
        </div>
      </div>
    {/if}
    {#if error || GetPassBundles.error}
      <p class="error">
        {#if error}
          {@render error()}
        {:else}
          <strong>{text`Could not find passes.`}</strong>
          {text`Please ensure that all passenger details are correct.`}
        {/if}
      </p>
    {/if}
  </div>
  <div slot="footer" class="footer">
    {#if bundle}
      <div class="summary">
        <div>
          <h3 class="heading">{text`Summary`}</h3>
          <p>
            {#if bundle}
              {getBundleTravelDays(bundle, text)}
              {getBundleValidity(bundle, text)},
              {text(bundle.class)}
              <br />
              {#if GetPassBundles.stale}
                <Loader length={8} />
              {:else}
                {@const total = bundle.passengers.reduce(
                  (acc, passenger) => acc + passenger.count,
                  0
                )}
                <span class="passengers">
                  <span class="icon">
                    <Symbol
                      name={total > 1 ? 'people' : 'person'}
                      size="fill" />
                  </span>
                  {#each bundle.passengers as { count, type }}
                    <span>{count}× {text(type)}</span>
                  {/each}
                </span>
              {/if}
            {:else if GetPassBundles.stale}
              <Loader length={8} />
              <br />
              <Loader length={4} />
            {/if}
          </p>
        </div>
        <p class="total">
          {text`Total`}
          <br />
          <strong class="sum">
            {#if GetPassBundles.stale || !bundle}
              <Loader length={3} />
            {:else}
              {#if !validate(passengers, departureDate, passengerInputMode)}
                <span class="from">{text`fr.`}</span>
              {/if}
              {money(bundle.price, WITHOUT_DECIMALS)}
            {/if}
          </strong>
        </p>
      </div>
      <Button
        validate
        type="submit"
        visuallyDisabled={disabled}
        disabled={loading || GetPassBundles.fetching}
        onclick={() => {
          if (!disabled && bundle) {
            onselect?.(bundle)
          }
        }}>
        {#if loading}
          <span class="spinner">
            <Symbol name="spinner" size="fill" prefix />
          </span>
          {text`Preparing`}
        {:else}
          {text`Proceed`}
        {/if}
      </Button>
    {/if}
    {#if credit}
      <div class="credit">
        {@render credit()}
      </div>
    {/if}
  </div>
</Modal>

<style>
  .pass-bundle-builder {
    container-type: inline-size;
    display: grid;
    gap: 2rem;
  }

  .title {
    line-height: 1.05;
    font-family: var(--heading-font-family);
    word-spacing: -0.05em;
    letter-spacing: -0.025em;
    font-weight: bold;
    font-size: var(--current-h2-font-size, 2em);
    color: rgb(var(--rgb-text));
    text-wrap: balance;
  }

  @media (min-width: 800px) {
    .title {
      padding-right: 5rem; /* Lave room for close button */
    }
  }

  .validity {
    font-size: 1.75rem;
    text-wrap: balance;
  }

  .body {
    display: grid;
    grid-template-rows: 1fr 1px 1fr;
    gap: 2rem;
  }

  @container (width >= 42rem) {
    .body {
      grid-template-rows: 1fr;
      grid-template-columns: 1fr 1px 1fr;
    }
  }

  .panel {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .divider {
    border: 0;
    background: rgba(var(--rgb-gray), 0.25);
  }

  .legend {
    display: block;
    color: rgb(var(--rgb-muted));
    font-size: 1rem;
    line-height: 2rem;
    font-weight: normal;
  }

  .options {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin-top: 0.5rem;
  }

  .option {
    flex: 1 1 auto;
  }

  .label {
    display: flex;
    flex-direction: column;
    align-items: baseline;
    gap: 0.5rem;
    width: 100%;
    padding: 0.6875rem 1rem;
    position: relative;
    background: rgba(var(--rgb-green), 0);
    border: 1.5px solid rgba(var(--rgb-light-gray), 0.6);
    border-radius: var(--border-radius-md);
    transition: var(--box-shadow-transition);
    cursor: pointer;
    text-align: left;
    word-spacing: -0.05em;
    transition:
      var(--box-shadow-transition),
      var(--background-transition),
      border-color 100ms var(--ease);
  }

  :global(.using-keyboard) .radio:focus-visible + .label {
    outline: solid var(--current-focus-ring-width, var(--focus-ring-width));
    outline-color: rgb(
      var(--current-focus-ring-color, var(--focus-ring-color))
    );
  }

  .radio:not(:disabled, :checked) + .label {
    box-shadow: var(--box-shadow-sm);
  }

  .radio:not(:disabled, :checked) + .label:hover {
    box-shadow: var(--box-shadow-sm-hover);
  }

  .radio:checked + .label {
    background: rgba(var(--rgb-green), 0.08);
    border-color: rgb(var(--rgb-green));
  }

  .radio:disabled + .label {
    color: rgba(var(--rgb-text), 0.6);
    cursor: not-allowed;
  }

  .price {
    font-size: 1.125rem;
  }

  .symbol {
    --current-color: rgb(var(--rgb-green));

    align-self: center;
    margin-left: auto;
    position: absolute;
    right: 0.75rem;
    top: 0.65rem;
  }

  .footer {
    display: flex;
    flex-direction: column;
  }

  .summary {
    display: flex;
    justify-content: space-between;
    margin-bottom: 1.5rem;
    color: rgb(var(--rgb-muted));
    font-size: 1rem;
    text-wrap: balance;
  }

  .passengers {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .departure-date {
    margin-top: 1.5rem;
  }

  .departure-date .aligned {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .icon {
    padding: 0.13rem 0.26rem;
    width: 1.2rem;
    height: auto;
    border-radius: 0.25rem;
    background: rgba(var(--rgb-light-gray), 0.3);
    color: rgb(107, 113, 139);
    transform: translateY(0.025rem);
  }

  .heading {
    font-size: 1.25rem;
    color: rgb(var(--rgb-text));
    font-weight: 600;
  }

  .total {
    text-align: right;
  }

  .sum {
    font-size: 2rem;
    font-weight: bold;
    color: rgb(var(--rgb-text));
  }

  .from {
    font-weight: normal;
  }

  .error {
    padding: 1rem;
    border-radius: var(--border-radius-sm);
    background: rgba(var(--rgb-red), 0.05);
    border: 1px solid rgba(var(--rgb-red), 0.6);
    color: rgb(var(--rgb-red));
  }

  .spinner {
    height: 1em;
    margin-right: 0.25em;
  }

  .credit {
    max-width: 35rem;
    margin: 0.75rem auto -1.5rem; /* Undo footer margin */
    padding-bottom: 1rem; /* Undo footer margin */
    text-align: center;
    color: rgb(var(--rgb-muted));
    font-size: 0.75rem;
  }

  .credit:first-child {
    padding: 0;
    margin: 0 auto;
  }

  .credit :global(a) {
    text-decoration: underline;
  }
</style>
